import { customElement } from 'lit/decorators.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { html, LitElement, TemplateResult } from 'lit'
import { consume } from '@lit/context'
import {
  activeQuotationContext,
  NotFetchedYet
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { NotFoundError } from 'components/ordering/quotation/api/dtos/error.js'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'


initializeIconsByRole({
  'csc-quotation-request-header.download-button': { name: 'arrow-down-to-line', style: 'solid' },
})


@customElement('csc-quotation-request-download-button')
export class CSCQuotationRequestDownloadButton extends LitElement {

  @consume({ context: activeQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError | NotFetchedYet

  private get url(): string {
    if (isQuotationDTO(this.quotation) && this.quotation.quotationDocumentUrl) return this.quotation.quotationDocumentUrl

    throw new Error('No document url found for quotation')
  }

  private ensureResponseOk(response: Response): void {
    if (!response.ok) {
      throw new Error(`Network response was not ok (${response.status})`)
    }
  }

  private filenameFromContentDisposition(header: string | null): string | null {
    if (!header) return null
    const match = /filename\*=UTF-8''([^;\n\r]+)/i.exec(header) || /filename="?([^";]+)"?/i.exec(header)
    if (match && match[1]) {
      try {
        return decodeURIComponent(match[1])
      } catch {
        return match[1]
      }
    }
    return null
  }

  private filenameFromUrl(url: string): string | null {
    try {
      const parsed = new URL(url)
      return parsed.pathname.split('/').pop() || null
    } catch {
      return null
    }
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const blobUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = blobUrl
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
  }

  private determineFilename(url: string, response: Response): string {
    const headerFilename = this.filenameFromContentDisposition(response.headers.get('content-disposition'))
    if (headerFilename) return headerFilename

    const urlFilename = this.filenameFromUrl(url)
    if (urlFilename) return urlFilename

    return `quotation-${(this.quotation as QuotationDTO).id}.pdf`
  }

  private async downloadQuotationDocument(): Promise<void> {
    try {
      const response = await fetch(this.url, { credentials: 'same-origin' })
      this.ensureResponseOk(response)

      const blob = await response.blob()
      const filename = this.determineFilename(this.url, response)

      this.triggerDownload(blob, filename)
    } catch (err) {
      console.error('Error downloading quotation document', err)
      alert(t('quotation_request_header_download_failed', {
        ns: 'csc-quotation-request-header',
        default: 'Failed to download document'
      }))
    }
  }

  protected override render(): TemplateResult {
    return html`
      <cse-chip @click=${this.downloadQuotationDocument}>
        <cse-icon icon-role="csc-quotation-request-header.download-button" slot="prefix"></cse-icon>
        ${t('quotation_request_header_download_button', {
          ns: 'csc-quotation-request-header',
          default: 'Download'
        })}
      </cse-chip>
    `
  }
}
